import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateInvestorCompany } from 'src/store/User/user.slice';
import {
  PersonalInformationContainer,
  Banner,
  FormDataBiometricsFiles,
  CompanyInfo,
  Container
} from './biometrics.styles';
import Card from 'src/utilities/UILibrary/UI/Card/Card';
import { InputField } from 'src/utilities/UILibrary/Forms/InputField/InputField';
import { Button } from 'src/utilities/UILibrary/UI/Button/Button';
import { RootState, useAppThunkDispatch } from 'src/store/store';
import { MetamapButton } from 'src/components/shared/ MetamapButton/MetamapButton';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { Checkbox } from 'src/utilities/UILibrary/Forms/Checkbox/Checkbox';
import { FormValues } from './biometrics.types';
import { useManageSteps } from 'src/hooks/useManageSteps';
import economicActivitiesGeneral from 'src/constants/economicActivitiesGeneral';
import economicActivitiesMapping from 'src/constants/economicActivitiesMapping';

export default function BiometricsMoral() {
  const user = useSelector((state: RootState) => state.user.currentUser);
  const navigate = useNavigate();
  const dispatch = useAppThunkDispatch();
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    register
  } = useForm<FormValues>();
  const [metamapComplete, setMetamapComplete] = useState(false);
  const { nextStep } = useManageSteps();

  const closeFlow = () => {
    navigate('/');
  };

  const clientid = process.env.REACT_APP_METAMAP_CLIENTID;
  const flowid = process.env.REACT_APP_METAMAP_FLOWID;
  const metadata = {
    id: user.id,
    typeOfUser: 'investor',
    fixedLanguage: 'es'
  };
  const economicActivity = watch('economicActivity');

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const input = {
      type: 'investor_moral_onboarding_primerPaso',
      input: {
        id: user.id,
        personalInformation: {
          RFC: data.RFCper
        },
        companyInformation: {
          RFC: data.RFC,
          denomination: data.denomination,
          companyName: data.companyName,
          generalActivity: 'Construcción',
          specificActivity: 'Residencial'
        },
        onboarding: {
          complete: false,
          currentStep: 2
        }
      }
    };
    await dispatch(updateInvestorCompany(input));
    await nextStep();
    navigate('/inversionista/onboarding-risks');
  };

  const optionsBusinessName = economicActivitiesGeneral.map((activity, index) => ({
    value: activity,
    label: activity,
    key: index
  }));

  const selectOptions = [
    { key: 1, value: 'SADECV', label: 'SA DE CV' },
    { key: 2, value: 'SAPIDECV', label: 'SAPI DE CV' },
    { key: 3, value: 'SC', label: 'SC' },
    { key: 4, value: 'SA', label: 'SA' }
  ];

  return (
    <PersonalInformationContainer>
      <Banner>
        <div>
          <figure>
            <img
              src={`${process.env.REACT_APP_BUCKET_FINSPHERA}/icon_app_dash_personalInformation_banner.png`}
              alt="Finsphera icono información personal"
            />
          </figure>
          <h2>Datos personales y biométricos del apoderado legal:</h2>
          <p>
            Vamos a completar tu registro como inversionista, para ello necesitamos que nos
            proporciones la siguiente información:
          </p>
        </div>
      </Banner>
      <Card className="card-container" isForm>
        <FormDataBiometricsFiles>
          <h3>Comencemos por los datos personales del apoderado legal:</h3>
          <div>
            <MetamapButton
              clientid={clientid}
              flowid={flowid}
              metadata={`{"id":"${metadata.id}","typeOfUser":"${metadata.typeOfUser}", "fixedLanguage":"${metadata.fixedLanguage}"}`}
              onUserFinishedSdk={() => setMetamapComplete(true)}
              disabled={metamapComplete}
            />
            <p>Necesitas tener tu INE a la mano</p>
          </div>
        </FormDataBiometricsFiles>
        <CompanyInfo>
          <h3>
            Proporciónanos la siguiente información y da click en el botón &quot;enviar&quot;:
          </h3>
          <div>
            <h3>Apoderado legal</h3>
            <Controller
              name="RFCper"
              control={control}
              rules={{
                minLength: {
                  value: 13,
                  message: 'RFC debe tener al menos 13 caracteres.'
                },
                required: 'Es un campo requerido.',
                pattern: {
                  value: /^[A-ZÑ0-9]+$/,
                  message: 'RFC inválido.'
                }
              }}
              render={({ field: { onChange, ...restFieldProps }, fieldState: { error } }) => (
                <InputField
                  maxLength={13}
                  label="Ingresa el RFC:"
                  placeholder="RFC"
                  autoUpperCase
                  onChangeInternalValue={onChange}
                  errorMessages={error && error.message ? [error.message] : []}
                  {...restFieldProps}
                />
              )}
            />

            <h3>Datos compañía o empresa</h3>
            <Controller
              name="companyName"
              control={control}
              rules={{
                required: 'Es un campo requerido.',
                pattern: {
                  value: /^[a-zA-ZÑñÁáÉéÍíÓóÚúÜü\s]+$/,
                  message: 'Nombre inválido.'
                }
              }}
              render={({ field: { onChange, ...restFieldProps }, fieldState: { error } }) => (
                <InputField
                  label="Ingresa el nombre de la compañía:"
                  placeholder="Nombre"
                  regex={/^[a-zA-ZÑñÁáÉéÍíÓóÚúÜü\s]+$/}
                  onChangeInternalValue={onChange}
                  errorMessages={error && error.message ? [error.message] : []}
                  wrapLabel
                  {...restFieldProps}
                />
              )}
            />

            <Controller
              name="RFC"
              control={control}
              rules={{
                minLength: {
                  value: 12,
                  message: 'RFC debe tener al menos 12 caracteres.'
                },
                required: 'Es un campo requerido.',
                pattern: {
                  value: /^[A-ZÑ0-9]+$/,
                  message: 'RFC inválido.'
                }
              }}
              render={({ field: { onChange, ...restFieldProps }, fieldState: { error } }) => (
                <InputField
                  label="Ingresa el RFC de la compañía:"
                  placeholder="RFC"
                  regex={/^[A-ZÑ0-9]+$/}
                  onChangeInternalValue={onChange}
                  autoUpperCase
                  maxLength={12}
                  errorMessages={error && error.message ? [error.message] : []}
                  wrapLabel
                  {...restFieldProps}
                />
              )}
            />

            <Controller
              name="denomination"
              control={control}
              rules={{
                required: 'Es un campo requerido.'
              }}
              render={({ field: { onChange, ...restFieldProps }, fieldState: { error } }) => (
                <InputField
                  label="Selecciona la razón social:"
                  placeholder="Seleccionar una opción"
                  isDropdown
                  options={selectOptions}
                  onChangeInternalValue={onChange}
                  errorMessages={error && error.message ? [error.message] : []}
                  {...restFieldProps}
                />
              )}
            />
            <Card.Row>
              <Controller
                name="economicActivity"
                control={control}
                defaultValue={undefined}
                rules={{ required: 'Es un campo requerido.' }}
                render={({ field, fieldState, ...rest }) => (
                  <InputField
                    label="Principal actividad económica:"
                    placeholder="Selecciona una opción"
                    isDropdown
                    options={optionsBusinessName}
                    onChangeInternalValue={field.onChange}
                    errorMessages={
                      fieldState.error && fieldState.error.message ? [fieldState.error.message] : []
                    }
                    {...rest}
                  />
                )}
              />
            </Card.Row>

            {economicActivity && economicActivitiesMapping[economicActivity] && (
              <Card.Row>
                <Controller
                  name="specificEconomicActivity"
                  control={control}
                  defaultValue={undefined}
                  rules={{ required: 'Es un campo requerido.' }}
                  render={({ field, fieldState, ...rest }) => (
                    <InputField
                      label="Selecciona tu actividad específica:"
                      placeholder="Selecciona una opción"
                      isDropdown
                      options={economicActivitiesMapping[economicActivity].map(({ name }) => ({
                        value: name,
                        label: name
                      }))}
                      onChangeInternalValue={field.onChange}
                      errorMessages={
                        fieldState.error && fieldState.error.message
                          ? [fieldState.error.message]
                          : []
                      }
                      {...rest}
                    />
                  )}
                />
              </Card.Row>
            )}
            <div className="checkbox">
              <Checkbox
                label="Acepto los Términos y Condiciones y el Aviso de Privacidad."
                {...register('termsAndPrivacy', { required: true })}
                boxSize="small"
                errorMessages={
                  errors.termsAndPrivacy && [
                    'Por favor acepta los términos y condiciones para continuar.'
                  ]
                }
              />
            </div>
          </div>
        </CompanyInfo>
      </Card>
      <Container>
        <Button size="small" onClick={closeFlow}>
          Cerrar
        </Button>
        <Button disabled={!metamapComplete} size="small" onClick={handleSubmit(onSubmit)}>
          Enviar
        </Button>
      </Container>
      <Container>
        <p>Si lo prefieres, puedes terminar la sincronización después.</p>
      </Container>
    </PersonalInformationContainer>
  );
}
